// BeatMXR audio engine — Web Audio graph, transport, sequencer, export.
// Instrument presets, sample maps, drum kits -> ./instruments
// Music theory helpers -> ./theory   |  DSP curves/impulse -> ./dsp   |  mixer strip -> ./channel
import { DRUM_VOICES } from "./drums";
import { TapRecorder, downloadBlob } from "./wav";
import { parseKeyToAllowed, parseKeyRoot, keyIsMinor, snapMidiToScale, midiToFreq } from "./theory";
import { makeSatCurve, makeImpulse } from "./dsp";
import { INSTRUMENTS, NUM_PATTERNS, DRUM_KITS, SAMPLE_SETS, makeEmptyPattern } from "./instruments";
import { Channel } from "./channel";

// Backward-compatible re-exports for components importing from "@/audio/engine".
export { EQ_FREQS, EQ_LABELS, parseKeyToAllowed } from "./theory";
export { INSTRUMENTS, INSTRUMENT_LIST, NUM_PATTERNS, DRUM_KITS, DRUM_KIT_LIST, SAMPLE_SETS } from "./instruments";

export class AudioEngine {
  constructor() {
    this.ctx = null;
    this.channels = {};
    this.started = false;
    this.isPlaying = false;
    this.bpm = 120;
    this.currentStep = 0;
    this.nextNoteTime = 0;
    this.lookahead = 25;
    this.scheduleAhead = 0.1;
    this.timerId = null;
    this.metronomeOn = false;
    // drum patterns: 8 slots (A–H). Each pattern spans `patternBars` bars = patternBars*16 sixteenth-steps.
    this.patternBars = 2;
    this.patterns = Array.from({ length: NUM_PATTERNS }).map(() => makeEmptyPattern(this.patternBars * 16));
    this.currentPatternEdit = 0;
    this.pattern = this.patterns[0];
    this.songBars = 4;
    this.chain = new Array(Math.ceil(this.songBars / this.patternBars)).fill(0); // pattern index per pattern-slot
    this.sections = null; // named song sections (Intro/Verse/Hook/Drop…) driving the arrangement
    this.currentBar = 0;
    this.playingGlobalStep = -1;
    // recorded synth notes: { step (global 16th), midi, durSteps }
    this.noteClip = [];
    this.recordingNotes = false;
    this._recStart = {};
    this._undo = [];
    this._redo = [];
    this.voiceGain = {};
    this.voiceMute = {};
    DRUM_VOICES.forEach((v) => {
      this.voiceGain[v.id] = 0.9;
      this.voiceMute[v.id] = false;
    });
    this.swing = 0;
    this.roleInstruments = null;
    this.songKey = null;
    this.roleMute = { bass: false, chord: false, lead: false };
    this.decks = {};
    this.onStep = null;
    this.instrument = "grandreal";
    this.samples = {};
    this._sampleLoading = {};
    this.realDrums = true;
    this.drumBuffers = {};
    this._drumLoading = null;
    this.drumKit = "acoustic";
    this._drumKitCache = {};
    this.trackBuffers = { track1: null, track2: null };
    this.trackSources = [];
  }

  setTrackBuffer(trackId, buffer) {
    this.ensureStarted();
    this.trackBuffers[trackId] = buffer;
  }

  addTrack() {
    this.ensureStarted();
    const nums = Object.keys(this.channels)
      .filter((id) => /^track\d+$/.test(id))
      .map((id) => Number(id.slice(5)));
    const n = (nums.length ? Math.max(...nums) : 0) + 1;
    const id = `track${n}`;
    const colors = ["#a855f7", "#22d3ee", "#f97316", "#eab308", "#ec4899", "#14b8a6", "#8b5cf6", "#ef4444"];
    const color = colors[(n - 1) % colors.length];
    this.channels[id] = new Channel(this, id, `Track ${n}`, color);
    this.trackBuffers[id] = null;
    this.refreshSoloMute();
    return { id, name: `Track ${n}`, color };
  }

  removeTrack(id) {
    const ch = this.channels[id];
    if (!ch) return;
    try {
      ch.input.disconnect();
      ch.fader.disconnect();
      ch.analyser.disconnect();
    } catch (e) {}
    delete this.channels[id];
    delete this.trackBuffers[id];
    this.refreshSoloMute();
  }

  renameTrack(id, name) {
    if (this.channels[id]) this.channels[id].name = name;
  }

  // Route an <audio> element through the Player mixer channel (so EQ/FX apply).
  connectMediaElement(el) {
    this.ensureStarted();
    if (this._mediaSrc) return this._mediaSrc;
    const src = this.ctx.createMediaElementSource(el);
    src.connect(this.channels.player.input);
    this._mediaSrc = src;
    return src;
  }

  ensureStarted() {
    if (this.started) {
      if (this.ctx.state === "suspended") this.ctx.resume();
      return;
    }
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    this.ctx = ctx;
    this.impulse = makeImpulse(ctx, 2.2, 2.5);

    // Sum bus: every channel feeds here, then through the Master channel (EQ/FX) to output.
    this.master = ctx.createGain();
    this.master.gain.value = 1.0;
    // Master glue + brickwall limiter for the "auto-mix" punch. Master channel → glue → makeup → limiter → out.
    this.glueComp = ctx.createDynamicsCompressor();
    this.glueComp.threshold.value = 0;
    this.glueComp.ratio.value = 1;
    this.glueComp.attack.value = 0.008;
    this.glueComp.release.value = 0.18;
    this.glueComp.knee.value = 28;
    this.masterMakeup = ctx.createGain();
    this.masterMakeup.gain.value = 1.0;
    // Master path is TRANSPARENT by default — no baked-in coloration, so a clean file plays
    // back clean at flat EQ. "Warmth/glue" is opt-in via Auto-Mix (adds the saturation then).
    this.masterSat = ctx.createWaveShaper();
    this.masterSat.curve = null;
    this.masterSat.oversample = "4x";
    this.limiter = ctx.createDynamicsCompressor();
    this.limiter.threshold.value = -1.0;
    this.limiter.ratio.value = 20;
    this.limiter.attack.value = 0.004;
    this.limiter.release.value = 0.2;
    this.glueComp.connect(this.masterMakeup).connect(this.masterSat).connect(this.limiter).connect(ctx.destination);
    this.autoMix = false;
    this.humanize = 0;
    this.masterChannel = new Channel(this, "master", "Master", "#00e5ff", this.glueComp);
    this.masterChannel.state.volume = 0.9;
    this.master.connect(this.masterChannel.input);
    this.masterChannel.applySoloMute(false);
    this.masterAnalyser = this.masterChannel.analyser;

    [
      ["deckA", "Deck A", "#00e5ff"],
      ["deckB", "Deck B", "#ff007f"],
      ["drums", "Drums", "#ffb800"],
      ["synth", "Synth", "#00ff66"],
      ["player", "Player", "#818cf8"],
      ["track1", "Track 1", "#a855f7"],
      ["track2", "Track 2", "#22d3ee"],
    ].forEach(([id, name, color]) => {
      this.channels[id] = new Channel(this, id, name, color);
    });

    this.decks = {
      deckA: { buffer: null, source: null, playing: false, rate: 1, name: "—", loop: true },
      deckB: { buffer: null, source: null, playing: false, rate: 1, name: "—", loop: true },
    };
    // Headphone cue bus: taps each deck PRE-fader into a MediaStream so you can
    // pre-listen on a second output (headphones) while the main mix keeps playing.
    this.cueDest = ctx.createMediaStreamDestination();
    this.cueGains = {};
    ["deckA", "deckB"].forEach((id) => {
      const g = ctx.createGain();
      g.gain.value = 0;
      this.channels[id].input.connect(g);
      g.connect(this.cueDest);
      this.cueGains[id] = g;
    });
    this.cueStream = this.cueDest.stream;
    this.crossfade = 0;
    this.started = true;
    // Preload real grand-piano + drum-kit samples in the background.
    this.loadSamples("piano").catch(() => {});
    this.loadDrumKit().catch(() => {});
  }

  // Mobile audio unlock: browsers (esp. iOS) keep the AudioContext suspended
  // until a user gesture. Call this on the first touch/click to fully unlock.
  unlock() {
    this.ensureStarted();
    const ctx = this.ctx;
    if (ctx.state === "suspended") ctx.resume();
    if (this._unlocked) return;
    try {
      const buf = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      src.start(0);
      this._unlocked = true;
    } catch (e) {}
  }

  // ---- Real recorded drum kit ----
  loadDrumKit(kitId = this.drumKit) {
    const kit = DRUM_KITS[kitId] ? kitId : "acoustic";
    if (this._drumKitCache[kit]) {
      this.drumBuffers = this._drumKitCache[kit];
      return Promise.resolve();
    }
    if (this._drumLoading && this._drumLoadingKit === kit) return this._drumLoading;
    this.ensureStarted();
    const base = DRUM_KITS[kit].base;
    const fileMap = { kick: "kick", snare: "snare", clap: "clap", hat: "hihat", openhat: "openhat", tom: "tom" };
    const buffers = {};
    const p = Promise.all(
      Object.entries(fileMap).map(async ([id, file]) => {
        const res = await fetch(`${base}${file}.wav`);
        const ab = await res.arrayBuffer();
        buffers[id] = await this.ctx.decodeAudioData(ab);
      })
    ).then(() => {
      this._drumKitCache[kit] = buffers;
      if (this.drumKit === kit) this.drumBuffers = buffers;
    });
    this._drumLoading = p;
    this._drumLoadingKit = kit;
    return p;
  }

  drumSamplesReady() {
    return Object.keys(this.drumBuffers).length >= 6;
  }

  setDrumKit(kitId) {
    if (!DRUM_KITS[kitId]) return;
    this.drumKit = kitId;
    this.realDrums = true;
    return this.loadDrumKit(kitId).catch(() => {});
  }

  setRealDrums(on) {
    this.realDrums = on;
    if (on) this.loadDrumKit().catch(() => {});
  }

  // Auto-mix: gentle bus glue + makeup + subtle warmth so beats come out punchy & "finished".
  // OFF = fully transparent master (clean in = clean out). ON = the opt-in "finish" character.
  setAutoMix(on) {
    this.autoMix = !!on;
    if (on) {
      this.glueComp.threshold.value = -15;
      this.glueComp.ratio.value = 2.1;
      this.masterMakeup.gain.value = 1.22;
      this.masterSat.curve = makeSatCurve(0.06);
    } else {
      this.glueComp.threshold.value = 0;
      this.glueComp.ratio.value = 1;
      this.masterMakeup.gain.value = 1.0;
      this.masterSat.curve = null;
    }
  }

  // Humanize amount 0..1 — drives velocity variation, accents & micro-timing at playback.
  setHumanize(amount) {
    this.humanize = Math.max(0, Math.min(1, amount));
  }

  triggerDrum(id, time, gain = 1) {
    const v = DRUM_VOICES.find((x) => x.id === id);
    if (this.realDrums && this.drumBuffers[id]) {
      const src = this.ctx.createBufferSource();
      src.buffer = this.drumBuffers[id];
      const g = this.ctx.createGain();
      g.gain.value = gain;
      src.connect(g).connect(this.channels.drums.input);
      src.start(time);
    } else if (v) {
      v.play(this.ctx, this.channels.drums.input, time, gain);
    }
  }

  // ---- Real recorded samples (lazy load + memoized) ----
  loadSamples(name) {
    if (this.samples[name] && this.samples[name].loaded) return Promise.resolve();
    if (this._sampleLoading[name]) return this._sampleLoading[name];
    this.ensureStarted();
    const spec = SAMPLE_SETS[name];
    const buffers = {};
    const midis = [];
    const p = Promise.all(
      spec.notes.map(async ([file, midi]) => {
        const res = await fetch(`${spec.base}${file}.mp3`);
        const ab = await res.arrayBuffer();
        const buf = await this.ctx.decodeAudioData(ab);
        buffers[midi] = buf;
        midis.push(midi);
      })
    ).then(() => {
      midis.sort((a, b) => a - b);
      this.samples[name] = { buffers, midis, loaded: true };
    });
    this._sampleLoading[name] = p;
    return p;
  }

  samplesReady(name) {
    return !!(this.samples[name] && this.samples[name].loaded);
  }

  _nearestSample(name, midi) {
    const s = this.samples[name];
    let best = s.midis[0];
    for (const m of s.midis) if (Math.abs(m - midi) < Math.abs(best - midi)) best = m;
    return best;
  }

  getChannel(id) {
    return id === "master" ? this.masterChannel : this.channels[id];
  }

  refreshSoloMute() {
    const anySolo = Object.values(this.channels).some((c) => c.state.solo);
    Object.values(this.channels).forEach((c) => c.applySoloMute(anySolo));
    if (this.masterChannel) this.masterChannel.applySoloMute(false);
  }

  setMasterVolume(v) {
    this.masterChannel.setVolume(v);
  }

  setCrossfade(x) {
    this.crossfade = x;
    const p = (x + 1) / 2; // 0 = full A, 1 = full B
    this.channels.deckA.gainMul = Math.cos((p * Math.PI) / 2);
    this.channels.deckB.gainMul = Math.sin((p * Math.PI) / 2);
    this.refreshSoloMute();
  }

  setBpm(bpm) {
    this.bpm = bpm;
  }

  // ---- transport / scheduler ----
  secondsPerBeat() {
    return 60.0 / this.bpm;
  }

  scheduleStep(globalStep, time, pattern, patStep) {
    const step = patStep == null ? globalStep % 16 : patStep;
    const stepDur = this.secondsPerBeat() / 4;
    const hz = this.humanize || 0;
    // drums — with velocity accents, soft ghost dynamics, micro-timing & hi-hat ROLLS
    DRUM_VOICES.forEach((v) => {
      const val = pattern[v.id][step];
      if (val && !this.voiceMute[v.id]) {
        const accent = step % 4 === 0 ? 1.0 : step % 2 === 0 ? 0.9 : 0.78;
        const velJitter = 1 - Math.random() * hz * 0.3;
        const g = this.voiceGain[v.id] * (hz ? accent * velJitter : 1);
        // keep the kick dead-on; nudge everything else a hair off the grid
        const offset = hz && v.id !== "kick" ? (Math.random() - 0.5) * hz * 0.011 : 0;
        // A numeric step value >= 2 means a ROLL: retrigger that many times across the
        // step (the signature trap "brrrt" hi-hats), with a slight velocity taper.
        const rollN = typeof val === "number" && val >= 2 ? Math.min(6, Math.floor(val)) : 1;
        if (rollN >= 2) {
          for (let r = 0; r < rollN; r++) {
            const rt = time + offset + (r / rollN) * stepDur;
            const rg = g * (1 - r * (0.12 + Math.random() * 0.04));
            this.triggerDrum(v.id, Math.max(this.ctx.currentTime, rt), Math.max(0.05, rg));
          }
        } else {
          this.triggerDrum(v.id, Math.max(this.ctx.currentTime, time + offset), g);
        }
      }
    });
    // recorded synth notes
    const ROLE_VEL = { bass: 0.72, chord: 0.3, lead: 0.66 };
    this.noteClip.forEach((n) => {
      if (n.step === globalStep) {
        if (n.role && this.roleMute && this.roleMute[n.role]) return; // muted melodic part
        const instId = (this.roleInstruments && n.role && this.roleInstruments[n.role]) || null;
        let vel = (n.role && ROLE_VEL[n.role]) || 0.8;
        if (hz) vel *= 1 - Math.random() * hz * 0.28;
        const offset = hz ? (Math.random() - 0.5) * hz * 0.012 : 0;
        this.scheduleSynthNote(midiToFreq(n.midi), Math.max(this.ctx.currentTime, time + offset), Math.max(1, n.durSteps) * stepDur, instId, vel);
      }
    });
    // metronome (accent the bar downbeat)
    if (this.metronomeOn && step % 4 === 0) {
      const osc = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      osc.frequency.value = step % 16 === 0 ? 1600 : 1000;
      g.gain.setValueAtTime(0.3, time);
      g.gain.exponentialRampToValueAtTime(0.001, time + 0.05);
      osc.connect(g).connect(this.master);
      osc.start(time);
      osc.stop(time + 0.06);
    }
  }

  scheduler() {
    // iOS may re-suspend the context after brief silence; keep the clock alive.
    if (this.ctx.state === "suspended") {
      try {
        this.ctx.resume();
      } catch (e) {}
    }
    while (this.nextNoteTime < this.ctx.currentTime + this.scheduleAhead) {
      const stepDur = this.secondsPerBeat() / 4;
      const globalStep = this.currentBar * 16 + this.currentStep;
      const swingOffset = this.currentStep % 2 === 1 ? this.swing * stepDur * 0.6 : 0;
      const playTime = this.nextNoteTime + swingOffset;
      const instanceIdx = Math.floor(this.currentBar / this.patternBars);
      const pattern = this.patterns[this.chain[instanceIdx] || 0];
      const patStep = (this.currentBar % this.patternBars) * 16 + this.currentStep;
      this.scheduleStep(globalStep, playTime, pattern, patStep);
      this.nextNoteTime += stepDur;
      const drawStep = this.currentStep;
      const drawBar = this.currentBar;
      const drawGlobal = globalStep;
      const drawTime = playTime;
      const drawPatStep = patStep;
      const drawInstance = instanceIdx;
      setTimeout(() => {
        this.playingGlobalStep = drawGlobal;
        this.playingPatStep = drawPatStep;
        this.playingInstance = drawInstance;
        this.lastStepTime = drawTime;
        this.lastGlobalStep = drawGlobal;
        if (this.onStep) this.onStep(drawStep, drawBar, drawPatStep, drawInstance);
      }, Math.max(0, (drawTime - this.ctx.currentTime) * 1000));
      this.currentStep = (this.currentStep + 1) % 16;
      if (this.currentStep === 0) this.currentBar = (this.currentBar + 1) % this.songBars;
    }
    this.timerId = setTimeout(() => this.scheduler(), this.lookahead);
  }

  // ---- patterns / arranger ----
  patternSteps() {
    return this.patternBars * 16;
  }
  setPatternBars(bars) {
    bars = [1, 2, 4].includes(bars) ? bars : 2;
    if (bars === this.patternBars) return;
    const oldSteps = this.patternBars * 16;
    const newSteps = bars * 16;
    this.patternBars = bars;
    // Resize every pattern lane, tiling the old bar(s) across the new length (grow)
    // or truncating (shrink) so existing grooves are preserved.
    this.patterns = this.patterns.map((p) => {
      const np = {};
      DRUM_VOICES.forEach((v) => {
        const src = Array.isArray(p[v.id]) ? p[v.id] : [];
        const arr = new Array(newSteps).fill(false);
        for (let i = 0; i < newSteps; i++) arr[i] = !!src[i % oldSteps];
        np[v.id] = arr;
      });
      return np;
    });
    this.pattern = this.patterns[this.currentPatternEdit] || this.patterns[0];
    if (this.songBars < bars) this.songBars = bars;
    const instances = Math.ceil(this.songBars / bars);
    const chain = new Array(instances).fill(0);
    for (let i = 0; i < Math.min(instances, this.chain.length); i++) chain[i] = this.chain[i];
    this.chain = chain;
    this.currentBar = 0;
    this.currentStep = 0;
  }
  selectEditPattern(i) {
    this.currentPatternEdit = i;
    this.pattern = this.patterns[i];
  }
  setSongBars(n) {
    n = Math.max(this.patternBars, Math.min(32, n));
    // keep song length a whole number of pattern-slots
    n = Math.ceil(n / this.patternBars) * this.patternBars;
    const instances = Math.ceil(n / this.patternBars);
    const chain = new Array(instances).fill(0);
    for (let i = 0; i < Math.min(instances, this.chain.length); i++) chain[i] = this.chain[i];
    this.chain = chain;
    this.songBars = n;
    if (this.currentBar >= n) this.currentBar = 0;
  }
  setChainBar(slotIndex, patternIndex) {
    if (slotIndex >= 0 && slotIndex < this.chain.length) this.chain[slotIndex] = patternIndex;
  }

  // Rebuild the arrangement (chain + songBars) from an ordered list of named sections.
  // Each section = { id, label, pattern (0-based index A..H), bars }.
  applySections(sections) {
    const pb = this.patternBars || 2;
    const chain = [];
    (sections || []).forEach((s) => {
      const slots = Math.max(1, Math.ceil((s.bars || pb) / pb));
      const pIdx = Math.max(0, Math.min(NUM_PATTERNS - 1, s.pattern || 0));
      for (let i = 0; i < slots; i++) chain.push(pIdx);
    });
    if (chain.length === 0) return;
    this.chain = chain;
    this.songBars = chain.length * pb;
    this.sections = sections;
  }

  // ---- AI song starter: apply a generated beat + melody/bass in one shot ----
  async applySongStarter(data) {
    this.ensureStarted();
    // Real sample + drum-kit loads gathered here and awaited before returning, so the
    // beat plays on real instruments immediately (no "child's toy" synth-fallback first pass).
    const loads = [];
    // AI beats use 1-bar (16-step) patterns chained per bar — reset the grid length so
    // the scheduler indexes correctly regardless of the user's manual pattern-bars setting.
    this.patternBars = 1;
    // Pro Feel: make the generated beat groove & sound finished, not typed.
    this.setHumanize(0.2);
    this.setAutoMix(true);
    if (typeof data.bpm === "number") this.bpm = Math.max(40, Math.min(240, Math.round(data.bpm)));
    if (typeof data.key === "string") this.songKey = data.key;

    const emptyLane = () => new Array(16).fill(false);
    const ROLLABLE = { hat: true, openhat: true, snare: true, clap: true };
    const readPattern = (p) => {
      const out = {};
      DRUM_VOICES.forEach((v) => {
        const arr = Array.isArray(p[v.id]) ? p[v.id] : [];
        out[v.id] = Array.from({ length: 16 }, (_, i) => {
          const x = arr[i];
          if (ROLLABLE[v.id] && typeof x === "number" && x >= 2) return Math.min(6, Math.floor(x));
          return !!x;
        });
      });
      return out;
    };

    let totalBars;
    // NEW multi-pattern arranged format
    if (Array.isArray(data.patterns) && data.patterns.length) {
      const pats = data.patterns.slice(0, 8);
      for (let i = 0; i < 8; i++) {
        this.patterns[i] = i < pats.length ? readPattern(pats[i]) : DRUM_VOICES.reduce((o, v) => ((o[v.id] = emptyLane()), o), {});
      }
      // Build the arrangement chain from sections.
      let chain = [];
      const arr = Array.isArray(data.arrangement) ? data.arrangement : [];
      arr.forEach((sec) => {
        const idx = Math.max(0, Math.min(pats.length - 1, Math.round(sec.patternIndex || 0)));
        const bars = Math.max(1, Math.min(8, Math.round(sec.bars || 1)));
        for (let b = 0; b < bars; b++) chain.push(idx);
      });
      if (!chain.length) chain = [0, 0, 0, 0];
      chain = chain.slice(0, 16);
      totalBars = chain.length;
      this.songBars = totalBars;
      this.chain = chain;
      this.currentPatternEdit = 0;
      this.pattern = this.patterns[0];
    } else {
      // Backward-compat: single drum pattern looped for N bars.
      const bars = Math.max(1, Math.min(8, data.bars || 2));
      this.patterns[0] = readPattern(data.drums || {});
      this.currentPatternEdit = 0;
      this.pattern = this.patterns[0];
      this.setSongBars(bars);
      this.chain = new Array(bars).fill(0);
      totalBars = bars;
    }

    // Melody + bass + lead notes (global 16th steps across the whole arrangement).
    const notes = Array.isArray(data.notes) ? data.notes : [];
    const maxStep = totalBars * 16 - 1;
    const allowed = parseKeyToAllowed(data.key); // snap everything into the song key
    this.noteClip = notes
      .filter((n) => n && typeof n.midi === "number" && typeof n.step === "number")
      .map((n) => {
        const role = n.role === "bass" || n.role === "chord" || n.role === "lead" ? n.role : "lead";
        let midi = Math.max(24, Math.min(96, Math.round(n.midi)));
        midi = snapMidiToScale(midi, allowed);
        return {
          step: Math.max(0, Math.min(maxStep, Math.round(n.step))),
          midi,
          durSteps: Math.max(1, Math.min(16, Math.round(n.durSteps || 2))),
          role,
        };
      });
    // Route each musical role to its own instrument so the beat sounds like a band,
    // not one piano playing everything. AI can specify; otherwise use tasteful defaults.
    const hasRoles = this.noteClip.some((n) => n.role);
    if (hasRoles) {
      const ai = data.instruments || {};
      const pick = (id, fallback) => (INSTRUMENTS[id] ? id : fallback);
      this.roleInstruments = {
        bass: pick(ai.bass, "bassreal"),
        chord: pick(ai.chord, "rhodesreal"),
        lead: pick(ai.lead, "lead"),
      };
      this.roleMute = { bass: false, chord: false, lead: false }; // fresh beat plays all parts
      // PRELOAD the real samples for every part so the beat plays on real instruments
      // immediately instead of falling back to a synth piano.
      Object.values(this.roleInstruments).forEach((id) => {
        const inst = INSTRUMENTS[id];
        if (inst && inst.sampler && !this.samplesReady(inst.sampler)) loads.push(this.loadSamples(inst.sampler).catch(() => {}));
      });
    }
    // AI can pick a genre-appropriate drum kit; default to the current one.
    if (data.drumKit && DRUM_KITS[data.drumKit]) {
      this.drumKit = data.drumKit;
      this.realDrums = true;
      loads.push(this.loadDrumKit(data.drumKit).catch(() => {}));
    }
    if (typeof data.reverb === "number" && this.channels.synth) {
      this.channels.synth.setReverb(Math.max(0, Math.min(0.4, data.reverb)));
    } else if (hasRoles && this.channels.synth) {
      this.channels.synth.setReverb(0.14);
    }
    if (typeof data.swing === "number") this.swing = Math.max(0, Math.min(0.6, data.swing));
    this.pushHistory();
    // Wait for real instrument samples + drum kit so the very first playback is real.
    await Promise.all(loads);
  }

  // ---- note recording ----
  toggleRecordNotes() {
    this.recordingNotes = !this.recordingNotes;
    this._recStart = {};
    return this.recordingNotes;
  }
  captureNoteOn(midi) {
    if (!this.recordingNotes || !this.isPlaying) return;
    this._recStart[midi] = this.playingGlobalStep >= 0 ? this.playingGlobalStep : 0;
  }
  captureNoteOff(midi) {
    if (!this.recordingNotes || !this.isPlaying) return;
    if (this._recStart[midi] == null) return;
    const start = this._recStart[midi];
    const end = this.playingGlobalStep >= 0 ? this.playingGlobalStep : start;
    let durSteps = end - start;
    if (durSteps < 1) durSteps = 1;
    this.noteClip.push({ step: start, midi, durSteps });
    delete this._recStart[midi];
  }
  clearNotes() {
    this.noteClip = [];
  }

  // ---- undo / redo ----
  pushHistory() {
    if (!this.started) return;
    this._undo.push(JSON.stringify(this.serialize()));
    if (this._undo.length > 40) this._undo.shift();
    this._redo = [];
  }
  undo() {
    if (!this._undo.length) return false;
    this._redo.push(JSON.stringify(this.serialize()));
    this.load(JSON.parse(this._undo.pop()));
    return true;
  }
  redo() {
    if (!this._redo.length) return false;
    this._undo.push(JSON.stringify(this.serialize()));
    this.load(JSON.parse(this._redo.pop()));
    return true;
  }

  setSwing(v) {
    this.swing = v;
  }
  setVoiceGain(id, v) {
    this.voiceGain[id] = v;
  }
  toggleVoiceMute(id) {
    this.voiceMute[id] = !this.voiceMute[id];
    return this.voiceMute[id];
  }
  setPattern(obj) {
    const steps = this.patternSteps();
    DRUM_VOICES.forEach((v) => {
      const src = obj[v.id] || [];
      const arr = new Array(steps).fill(false);
      // tile the 16-step preset across every bar; preserve numeric roll values
      for (let i = 0; i < steps; i++) {
        const x = src[i % 16];
        arr[i] = x ? x : false;
      }
      this.pattern[v.id] = arr;
    });
  }

  // Cycle a step through: off -> on -> roll x2 -> x3 -> x4 -> off.
  // Numeric values >= 2 play as a hi-hat/snare ROLL at playback.
  cycleStepRoll(voiceId, step) {
    const cur = this.pattern[voiceId][step];
    let next;
    if (!cur) next = 2;
    else if (cur === true || cur === 1) next = 2;
    else if (cur >= 4) next = false;
    else next = cur + 1;
    this.pattern[voiceId][step] = next;
    return next;
  }

  play() {
    this.ensureStarted();
    if (this.isPlaying) return;
    // iOS/mobile: the transport tap is a user gesture — fully unlock & resume the
    // audio clock here (key presses did this via noteOn, but Play never did), so the
    // scheduler doesn't time notes against a suspended/frozen AudioContext.
    this.unlock();
    if (this.ctx.state === "suspended") {
      try {
        this.ctx.resume();
      } catch (e) {}
    }
    this.isPlaying = true;
    this.currentStep = 0;
    this.currentBar = 0;
    this.nextNoteTime = this.ctx.currentTime + 0.12;
    // start any recorded/loaded track clips
    this.trackSources = [];
    Object.entries(this.trackBuffers).forEach(([trackId, buffer]) => {
      if (!buffer) return;
      const src = this.ctx.createBufferSource();
      src.buffer = buffer;
      src.loop = true;
      src.connect(this.channels[trackId].input);
      src.start(this.nextNoteTime);
      this.trackSources.push(src);
    });
    this.scheduler();
  }

  stop() {
    this.isPlaying = false;
    if (this.timerId) clearTimeout(this.timerId);
    this.timerId = null;
    this.currentStep = 0;
    this.currentBar = 0;
    this.playingGlobalStep = -1;
    this.trackSources.forEach((s) => {
      try {
        s.stop();
      } catch (e) {}
    });
    this.trackSources = [];
    if (this.onStep) this.onStep(-1);
  }

  toggleMetronome() {
    this.metronomeOn = !this.metronomeOn;
    return this.metronomeOn;
  }

  toggleStep(voiceId, step) {
    this.pattern[voiceId][step] = !this.pattern[voiceId][step];
    return this.pattern[voiceId][step];
  }

  // ---- live pad performance + finger-drum recording ----
  setDrumRecording(on) {
    this.drumRecording = !!on;
    return this.drumRecording;
  }

  // Play a pad now, and (if armed + playing) record the hit into the current
  // pattern, quantized to the nearest 16th step.
  playPad(voiceId) {
    this.ensureStarted();
    if (this.ctx.state === "suspended") this.ctx.resume();
    this.triggerDrum(voiceId, this.ctx.currentTime, this.voiceGain[voiceId] ?? 0.9);
    if (!(this.isPlaying && this.drumRecording)) return null;
    const stepDur = this.secondsPerBeat() / 4;
    const elapsed = this.ctx.currentTime - (this.lastStepTime || this.ctx.currentTime);
    const steps = Math.round(elapsed / stepDur);
    const globalStep = (this.lastGlobalStep || 0) + steps;
    const pSteps = this.patternSteps();
    const step = ((globalStep % pSteps) + pSteps) % pSteps;
    const pat = this.patterns[this.currentPatternEdit] || this.pattern;
    if (pat && pat[voiceId]) pat[voiceId][step] = true;
    return step;
  }

  clearPattern() {
    const steps = this.patternSteps();
    DRUM_VOICES.forEach((v) => (this.pattern[v.id] = new Array(steps).fill(false)));
  }

  // ---- turntables ----
  async loadDeck(deckId, arrayBuffer, name) {
    this.ensureStarted();
    const buffer = await this.ctx.decodeAudioData(arrayBuffer);
    this.decks[deckId].buffer = buffer;
    this.decks[deckId].name = name;
    this.decks[deckId].offset = 0;
    return buffer;
  }

  // Load an already-decoded AudioBuffer onto a deck (used for the mixed "instrumental").
  setDeckBuffer(deckId, buffer, name) {
    this.ensureStarted();
    this.decks[deckId].buffer = buffer;
    this.decks[deckId].name = name;
    this.decks[deckId].offset = 0;
    return buffer;
  }

  async decodeUrl(url) {
    this.ensureStarted();
    const res = await fetch(url);
    const ab = await res.arrayBuffer();
    return await this.ctx.decodeAudioData(ab);
  }

  async decodeFile(file) {
    this.ensureStarted();
    const ab = await file.arrayBuffer();
    return await this.ctx.decodeAudioData(ab.slice(0));
  }

  // Play a slice (region) of a buffer through the Synth channel so the
  // mixer EQ/FX/master all apply. Returns the source so callers can stop it.
  playSlice(buffer, offset, duration, gain = 1) {
    this.ensureStarted();
    this.unlock();
    if (this.ctx.state === "suspended") this.ctx.resume();
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    const g = this.ctx.createGain();
    g.gain.value = gain;
    src.connect(g).connect(this.channels.synth.input);
    const now = this.ctx.currentTime;
    src.start(now, Math.max(0, offset), Math.max(0.02, duration));
    return src;
  }

  // Sum multiple AudioBuffers into one (e.g. drums+bass+other = instrumental).
  mixBuffers(buffers) {
    this.ensureStarted();
    const len = Math.max(...buffers.map((b) => b.length));
    const out = this.ctx.createBuffer(2, len, this.ctx.sampleRate);
    for (let c = 0; c < 2; c++) {
      const od = out.getChannelData(c);
      for (const b of buffers) {
        const bd = b.getChannelData(Math.min(c, b.numberOfChannels - 1));
        for (let i = 0; i < bd.length; i++) od[i] += bd[i];
      }
    }
    return out;
  }

  startDeck(deckId) {
    const deck = this.decks[deckId];
    if (!deck.buffer || deck.playing) return;
    const src = this.ctx.createBufferSource();
    src.buffer = deck.buffer;
    src.loop = deck.loop !== false;
    src.playbackRate.value = deck.rate;
    src.connect(this.channels[deckId].input);
    const dur = deck.buffer.duration;
    const off = (((deck.offset || 0) % dur) + dur) % dur;
    src.start(0, off);
    deck.source = src;
    deck.startedAt = this.ctx.currentTime;
    deck.startOffset = off;
    deck.playing = true;
    // When looping is OFF, reset the deck to the start once the track finishes
    // playing on its own (ignore the onended that fires from a manual stop/pause).
    src.onended = () => {
      if (this.decks[deckId].source === src) {
        this.decks[deckId].source = null;
        this.decks[deckId].playing = false;
        this.decks[deckId].offset = 0;
        // Auto hand off to the other deck if it has a track loaded; otherwise just stop.
        const other = deckId === "deckA" ? "deckB" : "deckA";
        const od = this.decks[other];
        if (od && od.buffer && !od.playing) {
          od.offset = 0;
          this.startDeck(other);
          this.setCrossfade(other === "deckB" ? 1 : -1);
        }
        if (this.onDeckEnded) this.onDeckEnded(deckId);
      }
    };
  }

  setDeckLoop(deckId, on) {
    const deck = this.decks[deckId];
    if (!deck) return;
    deck.loop = !!on;
    if (!deck.source) return;
    deck.source.loop = !!on;
    if (!on && deck.playing && deck.buffer) {
      // Guarantee it stops at the natural end (loop=false alone is unreliable in some browsers).
      const rate = deck.source.playbackRate.value || 1;
      const dur = deck.buffer.duration;
      const elapsed = (this.ctx.currentTime - (deck.startedAt || this.ctx.currentTime)) * rate;
      const pos = (((deck.startOffset || 0) + elapsed) % dur + dur) % dur;
      const remaining = Math.max(0.05, (dur - pos) / rate);
      try { deck.source.stop(this.ctx.currentTime + remaining); } catch (e) {}
    }
  }

  // Pause: stop audio but REMEMBER the position so play resumes from here.
  pauseDeck(deckId) {
    const deck = this.decks[deckId];
    if (!deck.source) {
      deck.playing = false;
      return;
    }
    if (deck.buffer) {
      const elapsed = (this.ctx.currentTime - (deck.startedAt || this.ctx.currentTime)) * (deck.rate || 1);
      deck.offset = ((deck.startOffset || 0) + elapsed) % deck.buffer.duration;
    }
    try {
      deck.source.stop();
    } catch (e) {}
    deck.source = null;
    deck.playing = false;
  }

  // Cue: stop and rewind to the very beginning.
  cueDeck(deckId) {
    const deck = this.decks[deckId];
    if (deck.source) {
      try {
        deck.source.stop();
      } catch (e) {}
      deck.source = null;
    }
    deck.offset = 0;
    deck.playing = false;
  }

  stopDeck(deckId) {
    const deck = this.decks[deckId];
    if (deck.source) {
      try {
        deck.source.stop();
      } catch (e) {}
      deck.source = null;
    }
    deck.offset = 0;
    deck.playing = false;
  }

  setDeckRate(deckId, rate) {
    const deck = this.decks[deckId];
    if (deck.source && deck.buffer) {
      // capture progress at the old rate before switching, so pause stays accurate
      const elapsed = (this.ctx.currentTime - (deck.startedAt || this.ctx.currentTime)) * (deck.rate || 1);
      deck.startOffset = ((deck.startOffset || 0) + elapsed) % deck.buffer.duration;
      deck.startedAt = this.ctx.currentTime;
      deck.source.playbackRate.setTargetAtTime(rate, this.ctx.currentTime, 0.02);
    }
    deck.rate = rate;
  }

  // scratch: instant playback rate change (0..2.5)
  scratchDeck(deckId, rate) {
    const deck = this.decks[deckId];
    if (deck.source) {
      deck.source.playbackRate.cancelScheduledValues(this.ctx.currentTime);
      deck.source.playbackRate.setValueAtTime(Math.max(0, rate), this.ctx.currentTime);
    }
  }

  // Headphone cue (pre-listen) toggle for a deck.
  setDeckCue(deckId, on) {
    const g = this.cueGains && this.cueGains[deckId];
    if (g) g.gain.setTargetAtTime(on ? 1 : 0, this.ctx.currentTime, 0.01);
  }

  isDeckCued(deckId) {
    const g = this.cueGains && this.cueGains[deckId];
    return !!(g && g.gain.value > 0.01);
  }

  // Live playback position (seconds) + duration for the waveform + timer.
  deckPosition(deckId) {
    const d = this.decks[deckId];
    if (!d || !d.buffer) return { cur: 0, dur: 0 };
    let cur = d.offset || 0;
    if (d.playing) {
      const elapsed = (this.ctx.currentTime - (d.startedAt || this.ctx.currentTime)) * (d.rate || 1);
      cur = ((d.startOffset || 0) + elapsed) % d.buffer.duration;
    }
    return { cur, dur: d.buffer.duration };
  }

  // Jump to a position (seconds) — used by clicking the waveform to find the spot.
  seekDeck(deckId, sec) {
    const d = this.decks[deckId];
    if (!d || !d.buffer) return;
    const dur = d.buffer.duration;
    const off = ((sec % dur) + dur) % dur;
    d.offset = off;
    if (d.playing) {
      if (d.source) {
        try {
          d.source.stop();
        } catch (e) {}
      }
      const src = this.ctx.createBufferSource();
      src.buffer = d.buffer;
      src.loop = true;
      src.playbackRate.value = d.rate;
      src.connect(this.channels[deckId].input);
      src.start(0, off);
      d.source = src;
      d.startedAt = this.ctx.currentTime;
      d.startOffset = off;
    }
  }

  // ---- synth ----
  setInstrument(id) {
    if (INSTRUMENTS[id]) {
      this.instrument = id;
      const inst = INSTRUMENTS[id];
      if (inst.sampler && !this.samplesReady(inst.sampler)) this.loadSamples(inst.sampler).catch(() => {});
    }
  }

  // Live-swap the instrument the AI beat uses for a given role (bass/chord/lead).
  // Takes effect on the next loop; preloads samples for sampled instruments.
  setRoleInstrument(role, id) {
    if (!INSTRUMENTS[id]) return;
    if (!this.roleInstruments) this.roleInstruments = { bass: "bassreal", chord: "epiano", lead: "lead" };
    this.roleInstruments = { ...this.roleInstruments, [role]: id };
    const inst = INSTRUMENTS[id];
    if (inst.sampler && !this.samplesReady(inst.sampler)) this.loadSamples(inst.sampler).catch(() => {});
  }

  // Mute/unmute a melodic part so you can strip the beat back to just drums.
  setRoleMute(role, on) {
    if (!this.roleMute) this.roleMute = { bass: false, chord: false, lead: false };
    this.roleMute = { ...this.roleMute, [role]: !!on };
    return this.roleMute[role];
  }
  toggleRoleMute(role) {
    return this.setRoleMute(role, !(this.roleMute && this.roleMute[role]));
  }
  // Load externally-imported notes (from MIDI/MusicXML) as the song's melody.
  importNotes({ notes, bpm, key, bars } = {}) {
    this.ensureStarted();
    if (typeof bpm === "number" && bpm > 0) this.bpm = Math.max(40, Math.min(240, Math.round(bpm)));
    if (typeof key === "string" && key) this.songKey = key;
    const clean = (notes || []).filter((n) => n && typeof n.midi === "number");
    this.noteClip = clean.map((n) => ({ step: Math.max(0, n.step | 0), midi: n.midi, durSteps: Math.max(1, n.durSteps || 1), role: n.role || "lead" }));
    const maxStep = this.noteClip.reduce((m, n) => Math.max(m, n.step + n.durSteps), 0);
    const computedBars = Math.max(1, Math.ceil(maxStep / 16));
    this.songBars = bars ? Math.max(computedBars, bars) : computedBars;
    // give imported parts real instruments (trumpet lead fits his son's world)
    if (!this.roleInstruments) this.roleInstruments = { bass: "bassreal", chord: "rhodesreal", lead: "trumpetreal" };
    this.roleMute = { bass: false, chord: false, lead: false };
    ["bassreal", "rhodesreal", "trumpetreal"].forEach((id) => {
      const inst = INSTRUMENTS[id];
      if (inst && inst.sampler) this.loadSamples(inst.sampler).catch(() => {});
    });
    this.pushHistory();
    return this.noteClip.length;
  }

  // Wipe all melodic notes (chords/bass/lead), keeping the drum beat intact.
  clearMelody() {
    this.noteClip = [];
  }

  // Drop a one-tap funk/soul BRASS STAB riff over the beat: rhythmic triad hits
  // that follow the song key. Replaces any existing chord notes so it stays clean.
  addHornStabs(instrumentId = "brassreal") {
    this.ensureStarted();
    const allowed = parseKeyToAllowed(this.songKey);
    let root = parseKeyRoot(this.songKey);
    if (root == null) {
      // infer root from the most common bass pitch class, else default A.
      const counts = {};
      this.noteClip.forEach((n) => {
        if (n.role === "bass") counts[n.midi % 12] = (counts[n.midi % 12] || 0) + 1;
      });
      const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
      root = top ? Number(top[0]) : 9;
    }
    const third = keyIsMinor(this.songKey) ? 3 : 4;
    let base = 60 + root;
    if (base > 67) base -= 12; // keep the stab in a punchy C4–G4 register
    const triad = [base, base + third, base + 7].map((m) => snapMidiToScale(m, allowed));
    // Funk stab rhythm: the syncopated off-beats.
    const stabSteps = [4, 7, 10, 14];
    const bars = Math.max(1, this.songBars || 4);
    this.noteClip = this.noteClip.filter((n) => n.role !== "chord");
    for (let b = 0; b < bars; b++) {
      const useSteps = b % 2 === 0 ? [4, 10] : stabSteps; // vary so it breathes
      useSteps.forEach((s) => {
        const gs = b * 16 + s;
        triad.forEach((m) => this.noteClip.push({ step: gs, midi: m, durSteps: 2, role: "chord" }));
      });
    }
    if (!this.roleInstruments) this.roleInstruments = { bass: "bassreal", chord: instrumentId, lead: "lead" };
    else this.roleInstruments = { ...this.roleInstruments, chord: instrumentId };
    this.roleMute = { ...(this.roleMute || { bass: false, lead: false }), chord: false };
    const inst = INSTRUMENTS[instrumentId];
    if (inst && inst.sampler) this.loadSamples(inst.sampler).catch(() => {});
    this.pushHistory();
    return this.noteClip.length;
  }

  playSynthNote(freq) {
    this.ensureStarted();
    const ctx = this.ctx;
    const t = ctx.currentTime;
    let inst = INSTRUMENTS[this.instrument] || INSTRUMENTS.lead;

    // Real recorded sample path (falls back to synth grand until samples finish loading).
    if (inst.sampler) {
      if (this.samplesReady(inst.sampler)) {
        const midi = Math.round(69 + 12 * Math.log2(freq / 440));
        const best = this._nearestSample(inst.sampler, midi);
        const src = ctx.createBufferSource();
        src.buffer = this.samples[inst.sampler].buffers[best];
        src.playbackRate.value = Math.pow(2, (midi - best) / 12);
        const amp = ctx.createGain();
        amp.gain.setValueAtTime(inst.gain != null ? inst.gain : 0.9, t);
        src.connect(amp).connect(this.channels.synth.input);
        src.start(t);
        return { source: src, amp, env: inst.env, sampler: true };
      }
      // Not loaded yet: fall back to a synth of the same family (not a one-size
      // grand) so bass still sounds like bass, etc., until samples arrive.
      const fb = inst.sampler === "bass" ? "bass" : ["sax", "trumpet", "flugelhorn", "trombone", "frenchhorn", "tuba", "bugle", "mutedtrumpet"].includes(inst.sampler) ? "lead" : "grand";
      inst = INSTRUMENTS[fb];
    }

    const filter = ctx.createBiquadFilter();
    filter.type = inst.filter ? inst.filter.type : "lowpass";
    filter.frequency.value = inst.filter ? inst.filter.freq : 20000;
    filter.Q.value = inst.filter && inst.filter.q ? inst.filter.q : 1;
    if (inst.filterEnv) {
      const base = inst.filter ? inst.filter.freq : 2000;
      filter.frequency.setValueAtTime(inst.filterEnv.peak, t);
      filter.frequency.exponentialRampToValueAtTime(Math.max(200, base), t + inst.filterEnv.decay);
    }

    const amp = ctx.createGain();
    amp.gain.value = 0;
    if (inst.sat) {
      const shaper = ctx.createWaveShaper();
      shaper.curve = makeSatCurve(inst.sat);
      shaper.oversample = "2x";
      filter.connect(shaper).connect(amp).connect(this.channels.synth.input);
    } else {
      filter.connect(amp).connect(this.channels.synth.input);
    }

    const oscs = [];
    const mods = [];
    inst.osc.forEach((o) => {
      const osc = ctx.createOscillator();
      osc.type = o.type;
      const targetF = freq * Math.pow(2, o.octave || 0);
      if (inst.pitchEnv) {
        const startF = Math.min(18000, targetF * Math.pow(2, inst.pitchEnv.semitones / 12));
        osc.frequency.setValueAtTime(startF, t);
        osc.frequency.exponentialRampToValueAtTime(Math.max(20, targetF), t + inst.pitchEnv.decay);
      } else {
        osc.frequency.value = targetF;
      }
      osc.detune.value = o.detune || 0;
      const og = ctx.createGain();
      og.gain.value = o.gain != null ? o.gain : 1 / inst.osc.length;
      if (o.fm) {
        const mod = ctx.createOscillator();
        mod.type = o.fm.type || "sine";
        mod.frequency.value = freq * (o.fm.ratio || 1);
        const mg = ctx.createGain();
        mg.gain.setValueAtTime(freq * (o.fm.index || 1), t);
        if (o.fm.indexDecay)
          mg.gain.exponentialRampToValueAtTime(Math.max(1, freq * 0.02), t + o.fm.indexDecay);
        mod.connect(mg).connect(osc.frequency);
        mod.start(t);
        mods.push(mod);
      }
      osc.connect(og).connect(filter);
      osc.start(t);
      oscs.push(osc);
    });

    const env = inst.env;
    const peak = inst.gain != null ? inst.gain : 0.3;
    amp.gain.setValueAtTime(0, t);
    amp.gain.linearRampToValueAtTime(peak, t + env.a);
    amp.gain.linearRampToValueAtTime(peak * env.s, t + env.a + env.d);

    return { oscs, mods, amp, env };
  }

  releaseSynthNote(voice) {
    if (!voice) return;
    const t = this.ctx.currentTime;
    const r = voice.env && voice.env.r != null ? voice.env.r : 0.2;
    voice.amp.gain.cancelScheduledValues(t);
    voice.amp.gain.setValueAtTime(voice.amp.gain.value, t);
    voice.amp.gain.linearRampToValueAtTime(0, t + r);
    if (voice.source) {
      try {
        voice.source.stop(t + r + 0.05);
      } catch (e) {}
    }
    if (voice.oscs)
      voice.oscs.forEach((o) => {
        try {
          o.stop(t + r + 0.05);
        } catch (e) {}
      });
    if (voice.mods)
      voice.mods.forEach((m) => {
        try {
          m.stop(t + r + 0.05);
        } catch (e) {}
      });
  }

  // Play a synth note at a scheduled time for a fixed duration (used by recorded note playback).
  scheduleSynthNote(freq, time, dur, instrumentId, vel = 1) {
    const ctx = this.ctx;
    let inst = INSTRUMENTS[instrumentId] || INSTRUMENTS[this.instrument] || INSTRUMENTS.lead;

    // Real recorded sample path for scheduled notes (falls back to synth grand until loaded).
    if (inst.sampler) {
      if (this.samplesReady(inst.sampler)) {
        const midi = Math.round(69 + 12 * Math.log2(freq / 440));
        const best = this._nearestSample(inst.sampler, midi);
        const src = ctx.createBufferSource();
        src.buffer = this.samples[inst.sampler].buffers[best];
        src.playbackRate.value = Math.pow(2, (midi - best) / 12);
        const amp = ctx.createGain();
        const peak = (inst.gain != null ? inst.gain : 0.9) * vel;
        const r = inst.env && inst.env.r != null ? inst.env.r : 0.3;
        const offT = time + dur;
        amp.gain.setValueAtTime(peak, time);
        amp.gain.setValueAtTime(peak, offT);
        amp.gain.linearRampToValueAtTime(0.0001, offT + r);
        src.connect(amp).connect(this.channels.synth.input);
        src.start(time);
        src.stop(offT + r + 0.05);
        return;
      }
      // Not loaded yet: fall back to a synth of the same family (not a one-size
      // grand) so bass still sounds like bass, etc., until samples arrive.
      const fb = inst.sampler === "bass" ? "bass" : ["sax", "trumpet", "flugelhorn", "trombone", "frenchhorn", "tuba", "bugle", "mutedtrumpet"].includes(inst.sampler) ? "lead" : "grand";
      inst = INSTRUMENTS[fb];
    }

    const filter = ctx.createBiquadFilter();
    filter.type = inst.filter ? inst.filter.type : "lowpass";
    filter.frequency.value = inst.filter ? inst.filter.freq : 20000;
    filter.Q.value = inst.filter && inst.filter.q ? inst.filter.q : 1;
    if (inst.filterEnv) {
      const base = inst.filter ? inst.filter.freq : 2000;
      filter.frequency.setValueAtTime(inst.filterEnv.peak, time);
      filter.frequency.exponentialRampToValueAtTime(Math.max(200, base), time + inst.filterEnv.decay);
    }
    const amp = ctx.createGain();
    amp.gain.value = 0;
    if (inst.sat) {
      const shaper = ctx.createWaveShaper();
      shaper.curve = makeSatCurve(inst.sat);
      shaper.oversample = "2x";
      filter.connect(shaper).connect(amp).connect(this.channels.synth.input);
    } else {
      filter.connect(amp).connect(this.channels.synth.input);
    }
    const oscs = [];
    const env = inst.env;
    const rel = env.r != null ? env.r : 0.2;
    const glide = inst.glide || 0;
    if (!this._glideFreq) this._glideFreq = {};
    const prevF = this._glideFreq[instrumentId];
    inst.osc.forEach((o) => {
      const osc = ctx.createOscillator();
      osc.type = o.type;
      const targetF = freq * Math.pow(2, o.octave || 0);
      if (glide > 0 && prevF) {
        osc.frequency.setValueAtTime(Math.max(20, prevF * Math.pow(2, o.octave || 0)), time);
        osc.frequency.exponentialRampToValueAtTime(Math.max(20, targetF), time + glide);
      } else if (inst.pitchEnv) {
        // 808/tom "knock": start high, drop fast to the fundamental.
        const startF = Math.min(18000, targetF * Math.pow(2, inst.pitchEnv.semitones / 12));
        osc.frequency.setValueAtTime(startF, time);
        osc.frequency.exponentialRampToValueAtTime(Math.max(20, targetF), time + inst.pitchEnv.decay);
      } else {
        osc.frequency.value = targetF;
      }
      osc.detune.value = o.detune || 0;
      const og = ctx.createGain();
      og.gain.value = o.gain != null ? o.gain : 1 / inst.osc.length;
      if (o.fm) {
        const mod = ctx.createOscillator();
        mod.type = o.fm.type || "sine";
        mod.frequency.value = freq * (o.fm.ratio || 1);
        const mg = ctx.createGain();
        mg.gain.setValueAtTime(freq * (o.fm.index || 1), time);
        if (o.fm.indexDecay)
          mg.gain.exponentialRampToValueAtTime(Math.max(1, freq * 0.02), time + o.fm.indexDecay);
        mod.connect(mg).connect(osc.frequency);
        mod.start(time);
        mod.stop(time + dur + rel + 0.1);
      }
      osc.connect(og).connect(filter);
      osc.start(time);
      oscs.push(osc);
    });
    const peak = (inst.gain != null ? inst.gain : 0.3) * vel;
    // Attack "click"/beater transient (808 punch) so it knocks on small speakers.
    if (inst.click) {
      const co = ctx.createOscillator();
      co.type = "sine";
      co.frequency.setValueAtTime(220, time);
      co.frequency.exponentialRampToValueAtTime(55, time + 0.02);
      const cg = ctx.createGain();
      cg.gain.setValueAtTime(inst.click * peak, time);
      cg.gain.exponentialRampToValueAtTime(0.0001, time + 0.03);
      co.connect(cg).connect(this.channels.synth.input);
      co.start(time);
      co.stop(time + 0.05);
    }
    amp.gain.setValueAtTime(0, time);
    amp.gain.linearRampToValueAtTime(peak, time + env.a);
    amp.gain.linearRampToValueAtTime(peak * env.s, time + env.a + env.d);
    const offT = time + dur;
    amp.gain.setValueAtTime(Math.max(0.0001, peak * env.s), offT);
    amp.gain.linearRampToValueAtTime(0.0001, offT + rel);
    oscs.forEach((o) => o.stop(offT + rel + 0.05));
    if (glide > 0) this._glideFreq[instrumentId] = freq;
  }

  // ---- recording a track from mic ----
  async recordTrack(trackId, durationMs, onBuffer) {
    this.ensureStarted();
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const srcNode = this.ctx.createMediaStreamSource(stream);
    srcNode.connect(this.channels[trackId].input);
    const rec = new TapRecorder(this.ctx, srcNode);
    rec.start();
    this._activeRec = { rec, stream, srcNode, trackId, onBuffer };
    return true;
  }

  stopRecordTrack() {
    const a = this._activeRec;
    if (!a) return;
    const blob = a.rec.stop();
    a.rec.dispose();
    a.stream.getTracks().forEach((t) => t.stop());
    try {
      a.srcNode.disconnect();
    } catch (e) {}
    blob.arrayBuffer().then((ab) =>
      this.ctx.decodeAudioData(ab).then((buf) => {
        if (a.onBuffer) a.onBuffer(a.trackId, buf, blob);
      })
    );
    this._activeRec = null;
  }

  // ---- export ----
  async exportMixdown(bars = 2) {
    this.ensureStarted();
    const rec = new TapRecorder(this.ctx, this.masterChannel.fader);
    const wasPlaying = this.isPlaying;
    rec.start();
    if (!wasPlaying) this.play();
    const dur = bars * 4 * this.secondsPerBeat() * 1000 + 400;
    await new Promise((r) => setTimeout(r, dur));
    const blob = rec.stop();
    rec.dispose();
    if (!wasPlaying) this.stop();
    downloadBlob(blob, "beatmxr-mixdown.wav");
  }

  async exportStems(bars = 2) {
    this.ensureStarted();
    const recs = Object.values(this.channels).map((c) => ({
      name: c.name,
      rec: new TapRecorder(this.ctx, c.fader),
    }));
    const wasPlaying = this.isPlaying;
    recs.forEach((r) => r.rec.start());
    if (!wasPlaying) this.play();
    const dur = bars * 4 * this.secondsPerBeat() * 1000 + 400;
    await new Promise((r) => setTimeout(r, dur));
    for (const r of recs) {
      const blob = r.rec.stop();
      r.rec.dispose();
      downloadBlob(blob, `beatmxr-stem-${r.name.replace(/\s+/g, "-").toLowerCase()}.wav`);
      await new Promise((res) => setTimeout(res, 300));
    }
    if (!wasPlaying) this.stop();
  }

  // ---- project serialize ----
  serialize() {
    const channels = {};
    Object.entries(this.channels).forEach(([id, c]) => (channels[id] = c.state));
    return {
      bpm: this.bpm,
      patterns: this.patterns,
      currentPatternEdit: this.currentPatternEdit,
      chain: this.chain,
      songBars: this.songBars,
      noteClip: this.noteClip,
      swing: this.swing,
      roleInstruments: this.roleInstruments,
      roleMute: this.roleMute,
      songKey: this.songKey,
      voiceGain: this.voiceGain,
      voiceMute: this.voiceMute,
      instrument: this.instrument,
      realDrums: this.realDrums,
      drumKit: this.drumKit,
      patternBars: this.patternBars,
      masterVolume: this.masterChannel.state.volume,
      master: this.masterChannel.state,
      crossfade: this.crossfade,
      sections: this.sections,
      channels,
    };
  }

  applyChannelState(c, s) {
    if (!c || !s) return;
    c.setVolume(s.volume);
    c.setPan(s.pan);
    c.state.mute = s.mute;
    c.state.solo = s.solo;
    if (Array.isArray(s.eq)) s.eq.forEach((db, i) => c.setEqBand(i, db));
    if (s.fx) {
      c.setDistortion(s.fx.distortion);
      c.setFilter(s.fx.filterFreq);
      c.setComp(s.fx.comp);
      c.setDelay(s.fx.delay);
      c.setReverb(s.fx.reverb);
    }
  }

  load(data) {
    this.ensureStarted();
    if (data.bpm) this.bpm = data.bpm;
    // Older saved songs (no patternBars) were 1-bar / 16-step patterns.
    this.patternBars = [1, 2, 4].includes(data.patternBars) ? data.patternBars : 1;
    if (data.patterns) {
      this.patterns = data.patterns;
      // Pad older projects up to the current slot count (match current pattern length).
      while (this.patterns.length < NUM_PATTERNS) this.patterns.push(makeEmptyPattern(this.patternBars * 16));
      this.currentPatternEdit = data.currentPatternEdit || 0;
      this.pattern = this.patterns[this.currentPatternEdit] || this.patterns[0];
    } else if (data.pattern) {
      this.patterns[0] = data.pattern;
      this.pattern = this.patterns[0];
    }
    if (data.chain) this.chain = data.chain;
    if (typeof data.songBars === "number") this.songBars = data.songBars;
    if (Array.isArray(data.sections)) this.sections = data.sections;
    if (data.noteClip) this.noteClip = data.noteClip;
    if (data.roleInstruments) this.roleInstruments = data.roleInstruments;
    if (typeof data.songKey === "string") this.songKey = data.songKey;
    this.roleMute = data.roleMute && typeof data.roleMute === "object"
      ? { bass: false, chord: false, lead: false, ...data.roleMute }
      : { bass: false, chord: false, lead: false };
    if (typeof data.swing === "number") this.swing = data.swing;
    if (data.voiceGain) this.voiceGain = data.voiceGain;
    if (data.voiceMute) this.voiceMute = data.voiceMute;
    if (data.instrument) this.instrument = data.instrument;
    if (typeof data.realDrums === "boolean") this.realDrums = data.realDrums;
    if (data.drumKit && DRUM_KITS[data.drumKit]) {
      this.drumKit = data.drumKit;
      this.loadDrumKit(data.drumKit).catch(() => {});
    }
    if (data.master) this.applyChannelState(this.masterChannel, data.master);
    else if (typeof data.masterVolume === "number") this.setMasterVolume(data.masterVolume);
    if (typeof data.crossfade === "number") this.setCrossfade(data.crossfade);
    if (data.channels) {
      Object.entries(data.channels).forEach(([id, s]) => {
        this.applyChannelState(this.channels[id], s);
      });
      this.refreshSoloMute();
    }
  }
}

export const engine = new AudioEngine();
if (typeof window !== "undefined") window.__engine = engine;
